<script lang="ts">
	import { currencies, type CurrencyCode } from '$lib/utils/pricing';
	import { pricingState } from '$lib/stores/pricing.svelte';
	import { onMount, onDestroy } from 'svelte';

	interface Props {
		onCurrencyChange?: (currency: CurrencyCode) => void;
	}

	let { onCurrencyChange }: Props = $props();

	let isOpen = $state(false);

	function selectCurrency(currency: CurrencyCode) {
		pricingState.setCurrency(currency);
		onCurrencyChange?.(currency);
		isOpen = false;
	}

	function toggleDropdown() {
		isOpen = !isOpen;
	}

	// Close dropdown when clicking outside
	function handleClickOutside(e: MouseEvent) {
		const target = e.target as HTMLElement;
		if (!target.closest('.currency-selector')) {
			isOpen = false;
		}
	}

	onMount(() => {
		if (typeof window !== 'undefined') {
			window.addEventListener('click', handleClickOutside);
		}
	});

	onDestroy(() => {
		if (typeof window !== 'undefined') {
			window.removeEventListener('click', handleClickOutside);
		}
	});
</script>

<div class="currency-selector relative">
	<button
		type="button"
		onclick={toggleDropdown}
		class="flex items-center gap-2 px-4 py-2.5 min-h-11 bg-ak-surface border border-ak-border-subtle rounded-lg hover:border-ak-wintergreen transition-all {pricingState.isTransitioning ? 'opacity-70 cursor-wait' : ''}"
		aria-label="Select currency"
		disabled={pricingState.isTransitioning}
	>
		<span class="text-sm font-medium">{pricingState.selectedCurrency}</span>
		{#if pricingState.isTransitioning}
			<svg class="w-4 h-4 text-ak-wintergreen animate-spin" fill="none" viewBox="0 0 24 24">
				<circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
				<path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
			</svg>
		{:else}
			<svg
				class="w-4 h-4 text-ak-text-muted transition-transform {isOpen ? 'rotate-180' : ''}"
				fill="none"
				viewBox="0 0 24 24"
				stroke="currentColor"
				stroke-width="2"
			>
				<path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
			</svg>
		{/if}
	</button>

	{#if isOpen}
		<div
			class="absolute right-0 mt-2 w-48 bg-ak-deep border border-ak-border-subtle rounded-lg shadow-lg z-50 py-2"
			role="menu"
		>
			{#each Object.entries(currencies) as [code]}
				<button
					type="button"
					onclick={() => selectCurrency(code as CurrencyCode)}
					class="w-full flex items-center gap-3 px-4 py-2.5 min-h-11 text-left hover:bg-ak-surface transition-colors {pricingState.selectedCurrency === code
						? 'bg-ak-wintergreen/10 text-ak-wintergreen'
						: ''}"
					role="menuitem"
				>
					<span class="text-sm font-medium">{code}</span>
					{#if pricingState.selectedCurrency === code}
						<svg class="w-4 h-4 ml-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
							<path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
						</svg>
					{/if}
				</button>
			{/each}
		</div>
	{/if}
</div>
